import Axios, { AxiosRequestConfig } from "axios";
import {
  ADMIN_CUSTOM_HEADER,
  API_PUBLIC_AUTHORIZATION_TOKEN,
  BASE_URL,
} from "../config/AppConfig";
import { IServiceMethods } from "../data/AppType";
import {
  LOGOUT_REASON_ENUM,
  SERVICE_METHODS,
} from "../utils/AppEnumerations";
import { LOGOUT_CURRENT_SESSION } from "./auth/AuthServices";
import { localStorageUtils } from "../utils/LocalStorageUtil";
import {
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
  DEFAULT_STATUS_CODE_UNKNOWN_ERROR,
  DEFAULT_STATUS_CODE_UNAUTHORIZED,
  DEFAULT_STATUS_CODE_SUCCESS,
  TOKEN_EXPIRED_KEY,
  TOKEN_INVALID_ACCESS_TOKEN,
  EVENT_KEY_SESSION_LIMIT,
} from "../utils/AppConstants";
import { API_ENDPOINTS } from "../utils/ApiEndPoint";
import { decryptData } from "../utils/AppFunctions";
import { routePublic } from "../routes/PublicRoutePath";

export const CONFIG = Axios.create({
  baseURL: BASE_URL,
});
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}
const createError = (error: any): Error =>
  error instanceof Error ? error : new Error(String(error));

CONFIG.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const eventKey = error?.response?.data?.data?.event_key;

    if (eventKey === EVENT_KEY_SESSION_LIMIT) {
      localStorage.clear();
      setTimeout(() => {
        window.location.href = "/" + routePublic.login;
      }, 1500);
    }

    if (
      error?.response?.data &&
      error.response.data.code === DEFAULT_STATUS_CODE_UNAUTHORIZED &&
      error.response?.data?.data?.event_key &&
      (error.response.data.data.event_key === TOKEN_EXPIRED_KEY ||
        error.response.data.data.event_key === TOKEN_INVALID_ACCESS_TOKEN)
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshToken = await localStorageUtils.getRefreshToken();
          if (!refreshToken) {
            return Promise.reject(createError(error));
          }
          const resToken = await Axios.get(
            BASE_URL + "/" + API_ENDPOINTS.renew_access_token.url,
            {
              headers: {
                Authorization: `${refreshToken}`,
                "Content-Type": "application/json",
                "x-custom-header": ADMIN_CUSTOM_HEADER,
              },
            }
          );
          if (
            resToken?.data?.code === DEFAULT_STATUS_CODE_SUCCESS &&
            resToken?.data?.data
          ) {
            const decryptedData = decryptData(resToken.data.data);
            const newTokens = JSON.parse(decryptedData || "");
            await localStorageUtils.setAccessToken(newTokens.token);
            await localStorageUtils.setRefreshToken(newTokens.refresh_token);
            isRefreshing = false;
            onRefreshed(newTokens.token);
            originalRequest.headers.Authorization = newTokens.token;
            return Axios(originalRequest);
          }
        } catch (e) {
          isRefreshing = false;
          LOGOUT_CURRENT_SESSION(LOGOUT_REASON_ENUM.SessionExpired);
          return Promise.reject(createError(e));
        }
      }
      return new Promise((resolve) => {
        refreshSubscribers.push((token) => {
          originalRequest.headers.Authorization = token;
          resolve(Axios(originalRequest));
        });
      });
    }
    return Promise.reject(createError(error));
  }
);

CONFIG.interceptors.request.use(async (config) => {
  const token = await getAuthorizationToken();
  config.headers.setAuthorization(token);
  if (!config.headers["Content-Type"])
    config.headers.setContentType("application/json");
  config.headers["x-custom-header"] = ADMIN_CUSTOM_HEADER;
  return config;
});

const getAuthorizationToken = async () => {
  const token = await localStorageUtils.getAccessToken();
  if (token) {
    return token;
  }
  return API_PUBLIC_AUTHORIZATION_TOKEN;
};

export const serviceMaker = async (
  url: string,
  method: IServiceMethods,
  data: any = {},
  config: AxiosRequestConfig = {}
) => {
  try {
    let result;
    let APIInstance = CONFIG;
    switch (method) {
      case SERVICE_METHODS.GET: {
        result = await APIInstance.get(url);
        break;
      }
      case SERVICE_METHODS.GET_CONFIG: {
        result = await APIInstance.get(url, config);
        break;
      }
      case SERVICE_METHODS.POST: {
        result = await APIInstance.post(url, data);
        break;
      }
      case SERVICE_METHODS.POST_CONFIG: {
        result = await APIInstance.post(url, data, config);
        break;
      }
      case SERVICE_METHODS.PUT: {
        result = await APIInstance.put(url, data);
        break;
      }
      case SERVICE_METHODS.PUT_CONFIG: {
        result = await APIInstance.put(url, data, config);
        break;
      }
      case SERVICE_METHODS.DELETE: {
        result = await APIInstance.delete(url, data);
        break;
      }
      case SERVICE_METHODS.DELETE_CONFIG: {
        result = await APIInstance.delete(url, data);
        break;
      }
      case SERVICE_METHODS.REQUEST: {
        result = await APIInstance.request({
          url: url,
          data: JSON.stringify(data),
          ...(config ?? {}),
        });
        break;
      }
      default: {
        throw new Error("InvalidMethod");
      }
    }

    return {
      data: result.data?.data,
      code: result.status,
      message: result.data?.message,
      response: result.data,
    };
  } catch (error: any) {
    try {
      return {
        data: error.response.data,
        code: error.response.status,
        message: error.response.data._server_messages
          ? JSON.parse(JSON.parse(error.response.data._server_messages)[0])
              .message
          : error.response.data.message,
      };
    } catch (e) {
      return {
        data: error,
        code: DEFAULT_STATUS_CODE_UNKNOWN_ERROR,
        message: MESSAGE_UNKNOWN_ERROR_OCCURRED,
      };
    }
  }
};
